import { useEffect } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type { AircraftType } from '../../types';
import { telemetryApi } from '../../services/telemetryApi';
import { reverseGeocodeBatch, reverseGeocode } from '../../services/geocodingApi';
import type maplibregl from 'maplibre-gl';
import type { TacticalEvent } from '../../components/dashboard/types';

interface UseDashboardTelemetryParams {
  selectedAircraft: AircraftType | null;
  locationCache: Record<string, string>;
  mapRef: MutableRefObject<maplibregl.Map | null>;
  isValidCoordinate: (lat: number | null | undefined, lng: number | null | undefined) => boolean;
  setTelemetryHistory: Dispatch<SetStateAction<TacticalEvent[]>>;
  setLoadingHistory: Dispatch<SetStateAction<boolean>>;
  setLocationCache: Dispatch<SetStateAction<Record<string, string>>>;
  setSelectedAircraftLocation: Dispatch<SetStateAction<string | null>>;
}

export const useDashboardTelemetry = ({
  selectedAircraft,
  locationCache,
  mapRef,
  isValidCoordinate,
  setTelemetryHistory,
  setLoadingHistory,
  setLocationCache,
  setSelectedAircraftLocation,
}: UseDashboardTelemetryParams) => {
  const HISTORY_GEOCODE_COORD_LIMIT = 80;
  const HISTORY_GEOCODE_MAX_NEW = 10;

  const normalizeCoordKey = (lat: number, lng: number) => `${lat.toFixed(4)},${lng.toFixed(4)}`;

  const normalizeKeyFromAny = (value: string) => {
    const [rawLat, rawLng] = String(value).split(',');
    const lat = Number(rawLat);
    const lng = Number(rawLng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return normalizeCoordKey(lat, lng);
  };

  const getFirstNumeric = (obj: any, keys: string[]) => {
    for (const key of keys) {
      const value = obj?.[key];
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
      }
    }
    return null;
  };

  useEffect(() => {
    if (!selectedAircraft?.tailNumber) {
      setTelemetryHistory([]);
      return;
    }

    let isMounted = true;

    const fetchHistory = async () => {
      setLoadingHistory(true);
      try {
        const history = await telemetryApi.getHistory(selectedAircraft.tailNumber, 72, 2000);
        console.log(`📊 Fetched ${history.length} telemetry records for ${selectedAircraft.tailNumber}`);

        const events: TacticalEvent[] = history.map((log: any) => {
          const timestampIso = String(log.timestamp || log.recordedAt || log.recorded_at || new Date().toISOString());
          const speed = getFirstNumeric(log, ['groundSpeed', 'speed', 'gs', 'ground_speed', 'groundSpeedKt', 'groundSpeedKnots']) || 0;
          const heading = getFirstNumeric(log, ['heading', 'hdg']) || 0;
          const altitude = getFirstNumeric(log, ['altitude', 'alt', 'altitudeFt']);
          const verticalSpeed = getFirstNumeric(log, ['verticalSpeed', 'vertical_speed', 'vs', 'verticalSpeedFpm']);

          let action = 'Position Update';
          let type: 'GROUND' | 'FLIGHT' | 'ALERT' = 'GROUND';

          if (speed > 50) {
            action = 'Enroute';
            type = 'FLIGHT';
          } else if (speed > 5) {
            action = 'Taxiing';
            type = 'GROUND';
          } else if (speed > 0) {
            action = 'Moving';
            type = 'GROUND';
          } else {
            action = 'Stationary';
            type = 'GROUND';
          }

          const lat = log.lat || 0;
          const lng = log.lng || 0;
          const coordKey = normalizeCoordKey(lat, lng);
          const location = locationCache[coordKey] || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;

          return {
            timestamp: timestampIso,
            action,
            location,
            altitude: (typeof altitude === 'number' && Number.isFinite(altitude)) ? altitude : null,
            speed,
            verticalSpeed,
            heading,
            type,
            lat,
            lng,
            coordKey,
          };
        });

        if (isMounted) setTelemetryHistory(events);

        const uniqueCoords = new Map<string, { lat: number; lng: number }>();
        events.forEach((ev: TacticalEvent) => {
          if (ev.lat && ev.lng && ev.coordKey && !locationCache[ev.coordKey]) {
            uniqueCoords.set(ev.coordKey, { lat: ev.lat, lng: ev.lng });
          }
        });

        if (uniqueCoords.size > 0 && isMounted) {
          const coordsArray = Array.from(uniqueCoords.values()).slice(0, HISTORY_GEOCODE_COORD_LIMIT);
          const batchSize = Math.min(coordsArray.length, HISTORY_GEOCODE_MAX_NEW);

          reverseGeocodeBatch(coordsArray, batchSize)
            .then((geocodeResults) => {
              if (!isMounted) return;

              setLocationCache((prevCache) => {
                const nextCache: Record<string, string> = { ...prevCache };
                let changed = false;

                Object.entries(geocodeResults).forEach(([rawKey, result]) => {
                  const normalizedKey = normalizeKeyFromAny(rawKey);
                  if (!normalizedKey || !result?.location) return;

                  if (nextCache[normalizedKey] !== result.location) {
                    nextCache[normalizedKey] = result.location;
                    changed = true;
                  }
                });

                if (changed) {
                  setTelemetryHistory((prev) =>
                    prev.map((ev) => {
                      if (ev.coordKey && nextCache[ev.coordKey] && ev.location !== nextCache[ev.coordKey]) {
                        return { ...ev, location: nextCache[ev.coordKey] };
                      }
                      return ev;
                    })
                  );
                }

                return changed ? nextCache : prevCache;
              });
            })
            .catch(() => {});
        }
      } catch (error) {
        console.error('Failed to fetch telemetry history:', error);
        setTelemetryHistory([]);
      } finally {
        setLoadingHistory(false);
      }
    };

    fetchHistory();
    return () => {
      isMounted = false;
    };
  }, [selectedAircraft?.tailNumber, setLoadingHistory, setLocationCache, setTelemetryHistory]);

  useEffect(() => {
    setSelectedAircraftLocation(null);
    const lat = selectedAircraft?.telemetry?.lat;
    const lng = selectedAircraft?.telemetry?.lng;
    if (!lat || !lng) return;
    reverseGeocode(lat, lng)
      .then((r) => setSelectedAircraftLocation(r.location))
      .catch(() => {});
  }, [selectedAircraft?.id, setSelectedAircraftLocation]);

  useEffect(() => {
    if (!selectedAircraft?.telemetry || !mapRef.current) return;

    const { lat, lng } = selectedAircraft.telemetry;
    if (isValidCoordinate(lat, lng)) {
      try {
        mapRef.current.flyTo({
          center: [lng, lat],
          zoom: 15,
          duration: 1500,
        });
      } catch (e) {
        console.warn('Failed to center map on aircraft:', e);
      }
    }
  }, [selectedAircraft?.id, isValidCoordinate, mapRef]);
};
